import { ButtonCategory } from "@components/atoms/Button/types";
import { ProductCategory, StockItemTypeEnum } from "@utils/constants";
import { Diamond } from "@src/types/DiamondDetail";
import { useTheme } from "styled-components/native";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { setListViewCustomisation } from "@redux/slices/appStateSlice";
import { ColumnStructure } from "./types";
import { useSelector } from "react-redux";
import { TFunction } from "i18next";
import { User } from "@src/types/user";

export const preventLeftClickWeb = (event: React.MouseEvent) => {
  if (event.metaKey || event.ctrlKey) {
    // Prevents executing other logic when opening in a new tab
    event.stopPropagation();
    return;
  }

  if (event.button === 0) {
    // Prevents the default navigation only for normal left-clicks
    event.preventDefault();
  }
};

export const summaryOptions = (
  priceMode: number,
  productType: string,
  isVdbMarketPlace: boolean,
  summarySectionData?: any
) => {
  let mList = summarySectionData || [
    {
      name: "num_of_pieces",
      label: "searchResult.summaryBox.numOfPieces.text",
    },
    {
      name: "total_ct",
      label: "searchResult.summaryBox.totalCarat.text",
    },
    {
      name: "disc",
      label: "searchResult.summaryBox.discount.text",
    },
    {
      name: "price_per_carat",
      label: "searchResult.summaryBox.pricePerCarat.text",
    },
    {
      name: "total_price",
      label: "searchResult.summaryBox.totalPrice.text",
    },
    {
      name: "average_total_price",
      label: "searchResult.summaryBox.averageTotalPrice.text",
    },
  ];

  let filteredList = mList.filter((option) => {
    if (productType === ProductCategory.JEWELRY) {
      return option.name !== "disc" && option.name !== "price_per_carat";
    } else if (productType === ProductCategory.GEMSTONE) {
      return option.name !== "disc";
    } else if (
      productType === ProductCategory.LAB_GROWN_DIAMOND &&
      isVdbMarketPlace
    ) {
      return option.name !== "disc";
    }

    return true;
  });

  filteredList = filteredList.filter((option) => {
    if (priceMode === PriceModes.ORANGE) {
      return option.name !== "disc";
    } else if (priceMode === PriceModes.GREEN) {
      return (
        option.name !== "disc" &&
        option.name !== "price_per_carat" &&
        option.name !== "total_price" &&
        option.name !== "average_total_price"
      );
    }

    return true;
  });

  return filteredList;
};

export const buttonCategory = (productType: string) => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return ButtonCategory.diam;
    case ProductCategory.GEMSTONE:
      return ButtonCategory.gem;
    case ProductCategory.JEWELRY:
      return ButtonCategory.jewelry;
    default:
      return ButtonCategory.lab;
  }
};

export const convertSubOptionsToJewelryPayload = (json: any, key: string) => {
  let newjson: any = { ...json };
  if (
    key === "metals" &&
    json[key] &&
    json[key].length &&
    Object.keys(json[key][0]).length
  ) {
    newjson[key] = Object.keys(json[key][0]);
    Object.keys(json[key][0]).forEach((style: string) => {
      newjson[style] = json[key][0][style];
    });
  } else if (json[key] && Object.keys(json[key]).length) {
    newjson[key] = Object.keys(json[key]);
    Object.keys(json[key]).forEach((style: string) => {
      newjson[style] = json[key][style];
    });
  }
  return newjson;
};
interface CustomisationState {
  diamond: ColumnStructure[];
  gemstone: ColumnStructure[];
  lab_grown_diamond: ColumnStructure[];
  jewelry: ColumnStructure[];
}

export const handleCustomization = (
  productType: string,
  jsonValue: ColumnStructure[],
  dispatch: any,
  currentCustomisation: CustomisationState[]
) => {
  if (!productType || !jsonValue) {
    console.error("Invalid parameters passed to handleCustomization.");
    return;
  }
  if (!currentCustomisation[productType]) {
    currentCustomisation[productType] = [];
  }

  const updatedCustomisation = {
    ...currentCustomisation,
    [productType]: jsonValue,
  };

  dispatch(setListViewCustomisation(updatedCustomisation));
};

export const getProductListViewData = (productType: string) => {
  const productData = useSelector(
    (state: any) => state?.appState?.listViewCustomisation
  );
  if (!productType) {
    return [];
  }
  return productData?.[productType] || [];
};

const defaultState: CustomisationState = {
  diamond: [],
  gemstone: [],
  lab_grown_diamond: [],
  jewelry: [],
};

export const getProductListViewAllData = () => {
  const productData = useSelector(
    (state: any) => state?.appState.listViewCustomisation
  );

  if (!productData) {
    return defaultState;
  }

  const completeProductData: CustomisationState = {
    ...productData,
  };

  return completeProductData;
};

export type CartTypeRequest = {
  price_mode: number;
  stock_item_id?: number;
  cart_type?: string;
  stock_type?: string;
  quantity?: number;
  stock_item_ids?: Array<number>;
  additional_attributes?: any;
  item_ids?: Array<number>;
  action_type?: string;
  ids?: Array<number>;
};

export const isConfigurableJewelryItem = (item?: Diamond | null): boolean =>
  !!item &&
  item.custom_design_configuration === true &&
  Array.isArray(item.display_configuration_v2) &&
  item.display_configuration_v2.length > 0;

export const isJewelryProductType = (
  productType?: string | ProductCategory | null
): boolean =>
  productType === ProductCategory.JEWELRY ||
  productType === ProductCategory.LABGROWN_JEWELRY;

export const isJewelryProductItem = (item?: Diamond | null): boolean =>
  isJewelryProductType(item?.type);

export const shouldOpenJewelryVariationModal = (
  allowJewelryVariationModal: boolean,
  items?: Diamond | Diamond[] | null,
  productType?: string | ProductCategory | null
): boolean => {
  if (!allowJewelryVariationModal) {
    return false;
  }
  if (isJewelryProductType(productType)) {
    return true;
  }
  const list = Array.isArray(items) ? items : items ? [items] : [];
  return list.some(isJewelryProductItem);
};

export const getStockItemType = (productType: ProductCategory) => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return StockItemTypeEnum.DIAMOND;
    case ProductCategory.GEMSTONE:
      return StockItemTypeEnum.GEMSTONE;
    case ProductCategory.JEWELRY:
      return StockItemTypeEnum.JEWELRY;
    case ProductCategory.LAB_GROWN_DIAMOND:
      return StockItemTypeEnum.LAB_GROWN_DIAMOND;
    case ProductCategory.LABGROWN_JEWELRY:
      return StockItemTypeEnum.LABGROWN_JEWELRY;
    default:
      return ProductCategory.DIAMOND;
  }
};

export function normalizeSavedSearch(input) {
  let parsed = JSON.parse(input.replace(/“|”/g, '"'));

  if (
    typeof parsed.jewelry_styles === "object" &&
    Array.isArray(parsed.jewelry_styles) &&
    parsed.jewelry_styles.every(
      (style: any) =>
        parsed.hasOwnProperty(style) && Array.isArray(parsed[style])
    )
  ) {
    return parsed;
  }

  const normalized: Record<string, any> = {};

  for (const key in parsed) {
    if (
      key === "jewelry_styles" &&
      typeof parsed[key] === "object" &&
      !Array.isArray(parsed[key])
    ) {
      const subKeys = Object.keys(parsed[key]);
      normalized.jewelry_styles = subKeys;

      subKeys.forEach((subKey) => {
        normalized[subKey] = parsed[key][subKey];
      });
    } else {
      normalized[key] = parsed[key];
    }
  }
  return normalized;
}
export const useChangeViewOptions = (
  data?: [],
  priceMode?: number,
  t?: TFunction
) => {
  if (!data || typeof data !== "object") return [];
  const theme = useTheme();
  return Object.values(data)
    ?.filter(
      (view) =>
        Array.isArray(view?.price_modes) &&
        view?.price_modes?.includes(priceMode)
    )
    ?.filter((view) => {
      if (
        (theme?.isLargeScreen || theme.isTablet) &&
        view?.name === "list_expanded"
      ) {
        return false;
      }
      return true;
    })
    ?.sort((a, b) => a?.order - b?.order)
    ?.map((view) => {
      return {
        ...(view as Record<string, any>),
        label: t(view?.label),
      };
    });
};

export const showSubTitleInGridView = (
  customSearchData: any,
  sectionName: SearchFilterTypeEnum,
  user: User
) => {
  const gridViewData = customSearchData?.grid_view?.["sub-sections"];
  const priceMode = user?.price_mode;
  switch (sectionName) {
    case SearchFilterTypeEnum.SINGLE_STONES:
      return user?.feature_settings?.show_subtitle_on_grid_view;
    case SearchFilterTypeEnum.PAIRS:
      return (
        gridViewData?.[
          SearchFilterTypeEnumLowercase.PAIRS
        ]?.price_section?.options?.stock_num?.price_modes?.includes(
          priceMode
        ) ?? false
      );
    default:
      return user?.feature_settings?.show_subtitle_on_grid_view;
  }
};

export const getSubTitleText = (
  subTitle: string,
  showTwoLineSubTitle: boolean,
  productType: String
) => {
  if (showTwoLineSubTitle) {
    if (!subTitle?.trim()) return "-";
    const [first = "-", second = ""] = subTitle.split("|").map((s) => s.trim());
    return second ? `${first}${"\n"}${second}` : first;
  } else if (!showTwoLineSubTitle) {
    if (!subTitle) {
      return "-";
    }

    // Regular expression to find and replace numeric parts that are integers or decimals (VDB-30158)
    // Only if they are preceded by whitespace or at the start of the string (VDB-30655)
    if (
      productType === ProductCategory.DIAMOND ||
      productType === ProductCategory.LAB_GROWN_DIAMOND
    ) {
      return subTitle.replace(
        /(?<=\s|^)([0-9]+(?:\.[0-9]+)?)(?=\s|$)/,
        (match) => {
          if (match.length > 5 && !match.includes(".")) {
            return match;
          }
          return Math.round(parseFloat(match)).toString();
        }
      );
    } else {
      return subTitle;
    }
  } else {
    return subTitle;
  }
};

export const useShowSortingOption = (data?: [], priceMode?: number) => {
  if (!data || typeof data !== "object") return false;
  return data?.price_modes?.includes(priceMode);
};

export const filterDetailsByPriceMode = (data, priceMode?: number) => {
  return Object.values(data)
    .filter((item) => item?.price_modes?.includes(priceMode))
    .sort((a, b) => a?.order - b?.order);
};
